'use client';

import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { useEffect } from 'react';
import { AffirmScript, hasAffirmConfig } from './AffirmScript';
import styles from './AffirmMessage.module.scss';

/**
 * CTA types that qualify for Affirm messaging
 * - PRE_ORDER: informational message about split payment on the balance
 * - ORDER: the Affirm "as low as $X/month" widget
 */
type AffirmEligibleCta = 'PRE_ORDER' | 'ORDER';

/** Minimal shape of the global `affirm` object exposed by the SDK. */
interface AffirmGlobal {
  ui?: {
    ready?: (callback: () => void) => void;
    refresh?: () => void;
  };
}

export interface AffirmMessageProps {
  /** Current CTA type for the product */
  ctaType: string;
  /** Product price amount in dollars (not cents) */
  priceAmount?: number;
  /** Maximum allowed Affirm payment amount (from config) */
  affirmPaymentMax?: number;
  /** ISO country code used to pick the Affirm market config (defaults to US) */
  countryCode?: string;
  /** Additional CSS class */
  className?: string;
}

/**
 * Affirm financing message component for PDP.
 *
 * - PRE_ORDER: informational message about split payment + 0% financing.
 * - ORDER: renders the Affirm `affirm-as-low-as` widget node (populated by the
 *   SDK loaded via {@link AffirmScript}), gated on the market config being
 *   present so nothing empty is reserved when the keys are absent.
 *
 * @example
 * // Pre-order product
 * <AffirmMessage ctaType="PRE_ORDER" />
 *
 * @example
 * // Regular order with price
 * <AffirmMessage ctaType="ORDER" priceAmount={8500} affirmPaymentMax={30000} />
 */
export function AffirmMessage({
  ctaType,
  priceAmount,
  affirmPaymentMax,
  countryCode,
  className,
}: Readonly<AffirmMessageProps>) {
  const t = useTranslations('product.affirm');

  // The SDK only scans `.affirm-as-low-as` nodes at load time. On client-side
  // navigation (SKU/strap switch) the price changes but the DOM node is reused,
  // so re-run Affirm's refresh keyed on the amount. `ui.ready` fires immediately
  // if the SDK is already loaded, covering both first load and later navigations.
  useEffect(() => {
    const affirm = (window as unknown as { affirm?: AffirmGlobal }).affirm;
    affirm?.ui?.ready?.(() => affirm.ui?.refresh?.());
  }, [priceAmount]);

  // Only show for eligible CTA types
  const eligibleCtaTypes: AffirmEligibleCta[] = ['PRE_ORDER', 'ORDER'];
  if (!eligibleCtaTypes.includes(ctaType as AffirmEligibleCta)) {
    return null;
  }

  // PRE_ORDER: Show informational message about financing on remaining balance
  if (ctaType === 'PRE_ORDER') {
    return (
      <p className={clsx(styles.affirmMessage, className)} data-testid="affirm-message-preorder">
        {t('preorderMessage')}
      </p>
    );
  }

  // ORDER: render the Affirm widget when the price is within limits.
  if (ctaType === 'ORDER') {
    // Validate price is within Affirm limits
    const isPriceValid = affirmPaymentMax && priceAmount && priceAmount <= affirmPaymentMax;

    if (!isPriceValid && affirmPaymentMax) {
      // Price exceeds Affirm max - show generic financing message
      return (
        <p
          className={clsx(styles.affirmMessage, className)}
          data-testid="affirm-message-invalid-amount"
        >
          {t('invalidAmountMessage')}
        </p>
      );
    }

    // No price, or the market keys are not configured → render nothing (do not
    // reserve empty space for a widget the SDK can never populate).
    if (!priceAmount || !hasAffirmConfig(countryCode)) return null;

    // Render the Affirm "as low as $X/month" widget node. The Affirm SDK
    // (loaded by AffirmScript) scans for `.affirm-as-low-as` elements and fills
    // in the sentence using `data-amount` (price in cents). Ported from the
    // legacy AffirmSentence (className `affirm-as-low-as`, data-amount = price*100,
    // data-page-type "product").
    return (
      <>
        <AffirmScript countryCode={countryCode} />
        <p
          className={clsx(styles.affirmMessage, styles.widget, 'affirm-as-low-as', className)}
          data-amount={Math.round(priceAmount * 100)}
          data-page-type="product"
          data-testid="affirm-widget"
        />
      </>
    );
  }

  return null;
}

AffirmMessage.displayName = 'AffirmMessage';
